import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, InputNumber, Radio, Result, Row, Select, Statistic, Switch, message } from "antd";
import { CalculatorOutlined } from "@ant-design/icons";
import { useBts } from "./BtsStore";
import { money, toOptions } from "./helpers";

const BtsCalculator = () => {
    const { regions, getRegions, getCities, calculate } = useBts();
    const [form] = Form.useForm();
    const [senderCities, setSenderCities] = useState([]);
    const [receiverCities, setReceiverCities] = useState([]);
    const [useVolume, setUseVolume] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        getRegions();
    }, [getRegions]);

    const loadCities = async (regionCode, target) => {
        const list = await getCities(regionCode);
        if (target === "sender") setSenderCities(list);
        else setReceiverCities(list);
    };

    const onFinish = async (values) => {
        setLoading(true);
        setResult(null);
        try {
            const payload = {
                senderCityCode: String(values.senderCityCode),
                receiverCityCode: String(values.receiverCityCode),
                pickup_type: values.pickup_type,
                dropoff_type: values.dropoff_type,
                is_multiple_cost: values.is_multiple_cost ? 1 : 0,
                weight: Number(values.weight),
                ...(useVolume ? { volume: { x: Number(values.vx), y: Number(values.vy), z: Number(values.vz) } } : {}),
            };
            const data = await calculate(payload);
            const price = (data && typeof data === "object")
                ? (data.summaryPrice ?? data.price ?? data.cost ?? data.total)
                : data;
            setResult({ price, raw: data });
        } catch (e) {
            const errs = e?.response?.data?.errors;
            if (errs) Object.values(errs).flat().forEach((m) => message.error(String(m)));
            else message.error(e?.response?.data?.message || "Hisoblashda xatolik");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Row gutter={16}>
            <Col xs={24} lg={14}>
                <Card size="small" title={<span><CalculatorOutlined style={{ color: "var(--primary)", marginRight: 8 }} />Yetkazish narxini hisoblash</span>}>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{ pickup_type: "courier", dropoff_type: "courier", weight: 1, is_multiple_cost: false }}
                    >
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item label="Jo'natuvchi viloyat">
                                    <Select
                                        showSearch optionFilterProp="label" placeholder="Viloyat"
                                        options={toOptions(regions, ["code", "region_code", "id"], ["name", "nameuz", "title"])}
                                        onChange={(v) => { loadCities(v, "sender"); form.setFieldsValue({ senderCityCode: undefined }); }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="senderCityCode" label="Jo'natuvchi shahar" rules={[{ required: true, message: "Shaharni tanlang" }]}>
                                    <Select showSearch optionFilterProp="label" placeholder="Shahar"
                                        options={toOptions(senderCities, ["code", "city_code", "id"], ["name", "nameuz", "title"])} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label="Qabul qiluvchi viloyat">
                                    <Select
                                        showSearch optionFilterProp="label" placeholder="Viloyat"
                                        options={toOptions(regions, ["code", "region_code", "id"], ["name", "nameuz", "title"])}
                                        onChange={(v) => { loadCities(v, "receiver"); form.setFieldsValue({ receiverCityCode: undefined }); }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="receiverCityCode" label="Qabul qiluvchi shahar" rules={[{ required: true, message: "Shaharni tanlang" }]}>
                                    <Select showSearch optionFilterProp="label" placeholder="Shahar"
                                        options={toOptions(receiverCities, ["code", "city_code", "id"], ["name", "nameuz", "title"])} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item name="pickup_type" label="Olib ketish (pickup_type)" rules={[{ required: true }]}>
                                    <Radio.Group optionType="button" buttonStyle="solid">
                                        <Radio.Button value="courier">Kuryer</Radio.Button>
                                        <Radio.Button value="branch">Filial</Radio.Button>
                                        <Radio.Button value="self">O'zi</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="dropoff_type" label="Yetkazish (dropoff_type)" rules={[{ required: true }]}>
                                    <Radio.Group optionType="button" buttonStyle="solid">
                                        <Radio.Button value="courier">Kuryer</Radio.Button>
                                        <Radio.Button value="branch">Filial</Radio.Button>
                                        <Radio.Button value="self">O'zi</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item name="weight" label="Vazn, kg" rules={[{ required: true, message: "Vaznni kiriting" }]}>
                                    <InputNumber style={{ width: "100%" }} min={0.001} step={0.1} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="is_multiple_cost" label="Ko'p o'rinli (is_multiple_cost)" valuePropName="checked">
                                    <Switch checkedChildren="Ha" unCheckedChildren="Yo'q" />
                                </Form.Item>
                            </Col>

                            <Col xs={24}>
                                <Form.Item label="Hajm bo'yicha hisoblash (volume)">
                                    <Switch checked={useVolume} onChange={setUseVolume} checkedChildren="Yoqilgan" unCheckedChildren="O'chiq" />
                                </Form.Item>
                            </Col>
                            {useVolume && (
                                <>
                                    <Col xs={8}>
                                        <Form.Item name="vx" label="X (sm)" rules={[{ required: true }]}>
                                            <InputNumber style={{ width: "100%" }} min={0} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={8}>
                                        <Form.Item name="vy" label="Y (sm)" rules={[{ required: true }]}>
                                            <InputNumber style={{ width: "100%" }} min={0} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={8}>
                                        <Form.Item name="vz" label="Z (sm)" rules={[{ required: true }]}>
                                            <InputNumber style={{ width: "100%" }} min={0} />
                                        </Form.Item>
                                    </Col>
                                </>
                            )}
                        </Row>

                        <Button type="primary" size="large" htmlType="submit" icon={<CalculatorOutlined />} loading={loading}>
                            Hisoblash
                        </Button>
                    </Form>
                </Card>
            </Col>

            <Col xs={24} lg={10}>
                <Card size="small" title="Natija" style={{ height: "100%" }}>
                    {result ? (
                        <>
                            <Statistic
                                title="Yetkazish narxi"
                                value={result.raw?.all_cost != null ? money(result.raw.all_cost) : "—"}
                                suffix="so'm"
                                valueStyle={{ color: "var(--primary)", fontWeight: 700 }}
                            />
                            <pre style={{
                                marginTop: 16, background: "var(--bg-main)", border: "1px solid var(--border)",
                                borderRadius: "var(--radius-md)", padding: 12, fontSize: 12, maxHeight: "40vh", overflow: "auto",
                            }}>
                                {JSON.stringify(result.raw, null, 2)}
                            </pre>
                        </>
                    ) : (
                        <Result icon={<CalculatorOutlined style={{ color: "var(--border-hover)" }} />} subTitle="Hisoblash uchun formani to'ldiring" />
                    )}
                </Card>
            </Col>
        </Row>
    );
};

export default BtsCalculator;
